import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import DischargeChecklistProgress from 'components/shared/dischargeChecklist/DischargeChecklistProgress';
import Tooltip from 'components/shared/Tooltip';
import { capitalize } from 'lib/util';
import { DischargeChecklistState, DischargeChecklistSummary } from 'models/DischargeChecklistItem';
import LocationEpisode from 'models/LocationEpisode';
import { ReviewStatus } from 'models/Review';
import colors from 'styles/theme/colors';
import CircleCheckIcon from 'svg/CircleCheckIcon';
import DischargingToIcon from 'svg/DischargingToIcon';
import LocationPinIcon from 'svg/LocationPinIcon';
import RadioButtonUnchecked from 'svg/RadioButtonUnchecked';

export type PayerType = 'medicare' | 'other';

interface DischargeCalendarPatientCardProps {
  entry: LocationEpisode;
  payerType: PayerType;
  height?: number;
}

// Status only: the calendar never asks for notes, which can hold patient information.
function ChecklistTooltipSection({ checklist }: { checklist: DischargeChecklistSummary }) {
  return (
    <div data-testid='calendar-checklist-tooltip'>
      <TooltipChecklistTitle>
        Discharge Checklist ({checklist.done}/{checklist.total})
      </TooltipChecklistTitle>
      <TooltipChecklist>
        {checklist.items.map((item) => {
          const done = item.state === DischargeChecklistState.DONE;
          const notNeeded = item.state === DischargeChecklistState.NOT_NEEDED;

          return (
            <TooltipChecklistItem key={item.itemId} $muted={done || notNeeded} data-state={item.state}>
              {done ? (
                <CircleCheckIcon width={10} height={10} fill={colors.green100} aria-label='Done' />
              ) : (
                <RadioButtonUnchecked width={10} height={10} aria-label={notNeeded ? 'Not needed' : 'Open'} />
              )}
              <span>
                {item.label}
                {notNeeded && ' · Not needed'}
              </span>
            </TooltipChecklistItem>
          );
        })}
      </TooltipChecklist>
    </div>
  );
}

function PatientTooltip({ entry }: { entry: LocationEpisode }) {
  const latestReferral = entry.latestReferralReview;
  const referralStatusColor = (() => {
    switch (latestReferral?.status) {
      case ReviewStatus.ACCEPTED:
        return colors.green100;
      case ReviewStatus.REJECTED:
        return colors.red100;
      default:
        return colors.white;
    }
  })();

  const planningDischargeTo = entry.projectedDischargeReview?.planningDischargeTo;
  const referral = entry.latestReferralReview;
  const referralGroup = referral?.group;
  const keyDetails = entry.keyDetails;

  const lengthOfStay = entry.lengthOfStay;
  const projectedLengthOfStay = entry.projectedLengthOfStay;
  const checklist = entry.projectedDischargeReview?.visibleDischargeChecklist ?? null;

  return (
    <TooltipContainer>
      <TooltipPatientName>{entry.patient.name}</TooltipPatientName>
      <ul>
        <li>
          <TooltipSectionLabel>Length of Stay:</TooltipSectionLabel>{' '}
          <TooltipSectionValue>
            {lengthOfStay !== undefined && lengthOfStay !== null ? `${lengthOfStay} days` : '—'}
          </TooltipSectionValue>
        </li>
        <li>
          <TooltipSectionLabel>Projected Length of Stay:</TooltipSectionLabel>{' '}
          <TooltipSectionValue>
            {projectedLengthOfStay !== null && projectedLengthOfStay !== undefined
              ? `${projectedLengthOfStay} days`
              : '—'}
          </TooltipSectionValue>
        </li>
        {planningDischargeTo && (
          <li>
            <TooltipSectionLabel>Discharging To:</TooltipSectionLabel>{' '}
            <TooltipSectionValue>{planningDischargeTo}</TooltipSectionValue>
          </li>
        )}
        {referralGroup && (
          <>
            <li>
              <TooltipSectionLabel>{referralGroup.groupType.displayName}:</TooltipSectionLabel>{' '}
              <TooltipSectionValue>{referralGroup.name}</TooltipSectionValue>
            </li>
            <li>
              <TooltipSectionLabel>Referral Status:</TooltipSectionLabel>{' '}
              <TooltipSectionValue style={{ color: referralStatusColor }}>
                {capitalize(referral.statusLabel)}
              </TooltipSectionValue>
            </li>
          </>
        )}
      </ul>
      {keyDetails && (
        <>
          <TooltipDivider />
          <TooltipKeyDetails>{keyDetails}</TooltipKeyDetails>
        </>
      )}
      {checklist && (
        <>
          <TooltipDivider />
          <ChecklistTooltipSection checklist={checklist} />
        </>
      )}
    </TooltipContainer>
  );
}

export default function DischargeCalendarPatientCard({ entry, payerType, height }: DischargeCalendarPatientCardProps) {
  const navigate = useNavigate();

  const patientName = entry.patient.name ?? '';
  const isDischarged = entry.discharged;
  // As it was at discharge, for a discharged patient: nobody can change it after that.
  const checklist = entry.projectedDischargeReview?.visibleDischargeChecklist ?? null;

  const handleClick = () => {
    navigate(`/patients/${entry.episodeId}`);
  };

  const card = (
    <Card
      $payerType={payerType}
      $isDischarged={isDischarged}
      $height={height}
      onClick={handleClick}
      data-cy={`discharge-calendar-patient-card-${entry.id}`}>
      <PatientName>
        {patientName} ({entry.projectedLengthOfStay ?? '—'})
      </PatientName>
      {entry.group?.name && (
        <IconRow>
          <LocationPinIcon width={8} height={8} />
          <GroupName>
            {entry.group.name}
            {entry.group.groupType?.displayName ? ` (${entry.group.groupType.displayName})` : ''}
          </GroupName>
        </IconRow>
      )}
      <IconRow>
        <DischargingToIcon width={8} height={8} />
        <PlanningDischargeTo>{entry.projectedDischargeReview?.planningDischargeTo || '—'}</PlanningDischargeTo>
      </IconRow>
      {checklist && (
        <ChecklistRow data-testid='calendar-checklist-progress'>
          <DischargeChecklistProgress
            done={checklist.done}
            total={checklist.total}
            color={colors.black75}
            fontSize={10}
          />
        </ChecklistRow>
      )}
    </Card>
  );

  return (
    <Tooltip position='bottom' text={<PatientTooltip entry={entry} />}>
      {card}
    </Tooltip>
  );
}

const Card = styled.div<{ $payerType: PayerType; $isDischarged: boolean; $height?: number }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 10px;
  border-radius: 2px;
  cursor: pointer;
  transition: opacity 0.2s ease;
  opacity: ${({ $isDischarged }) => ($isDischarged ? 0.25 : 1)};

  background-color: ${({ $payerType, theme }) =>
    $payerType === 'medicare' ? theme.colors.green10 : theme.colors.lightBlue10};

  border-left: 3px solid
    ${({ $payerType, theme }) => ($payerType === 'medicare' ? theme.colors.green100 : theme.colors.lightBlue100)};
  height: ${({ $height }) => ($height ? `${$height}px` : 'auto')};
  flex-shrink: 0;

  &:hover {
    opacity: ${({ $isDischarged }) => ($isDischarged ? 0.35 : 0.9)};
  }
`;

const PatientName = styled.span`
  font-size: 12px;
  font-weight: var(--font-weight-medium);
  color: ${({ theme }) => theme.colors.black100};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
`;

const IconRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;

  svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.black75};
  }
`;

const GroupName = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.black75};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PlanningDischargeTo = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.black75};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TooltipContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 6px;

  ul {
    margin-top: 6px;
    padding-inline-start: 13px;
    li {
      text-indent: -2px;
    }
  }
`;

const TooltipPatientName = styled.span`
  font-size: 12px;
  font-weight: 500;
`;

const TooltipSectionLabel = styled.span`
  font-size: 10px;
`;

const TooltipSectionValue = styled.span`
  font-size: 10px;
  font-weight: 500;
`;

const TooltipDivider = styled.hr`
  width: 100%;
  border: 0;
  border-top: 1px solid ${colors.white50};
  margin: 8px 0;
`;

const TooltipKeyDetails = styled.div`
  font-size: 10px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const ChecklistRow = styled.div`
  margin-top: 4px;
`;

const TooltipChecklistTitle = styled.div`
  font-size: 10px;
  font-weight: 500;
  margin-bottom: 6px;
`;

// Doubled class so the tooltip's own bulleted-list styles (TooltipContainer's ul and li) don't apply.
const TooltipChecklist = styled.ul`
  && {
    display: flex;
    flex-direction: column;
    gap: 6px;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  && li {
    text-indent: 0;
  }
`;

const TooltipChecklistItem = styled.li<{ $muted: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  color: ${({ $muted }) => ($muted ? colors.white50 : colors.white)};
  white-space: pre-wrap;
  word-break: break-word;

  svg {
    flex-shrink: 0;
  }
`;
